import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { BsEye } from 'react-icons/bs';
import { Questions } from './questions';

interface Assessment {
  email: string;
  name: string;
  phoneNumber: string;
  submittedAt: string;
  responseCount: number;
  createdAt?: string;
  status?: string;
}

export const AssessmentDashboard: React.FC = () => {

  const [activeTab, setActiveTab] = useState<'assessment' | 'detailed'>('assessment');
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [detailedAssessments, setDetailedAssessments] = useState<Assessment[]>([])
  const [loading, setLoading] = useState(true)
  const verified = localStorage.getItem('verify')

  useEffect(() => {
    const fetchData = async() => {
        setLoading(true)
        try {
            // Fetch regular surveys
            const regularRes = await axios.get(`https://e6w2t1d2f1.execute-api.us-east-1.amazonaws.com/dev/surveys`)
            console.log('Regular survey data:', regularRes.data);

            // Extract surveys from the response
            const regularSurveys = regularRes.data.surveys || [];

            // Set regular surveys
            setAssessments(regularSurveys)

            // For now, set empty advanced surveys
            // TODO: Find the correct endpoint for advanced surveys
            setDetailedAssessments([])

            setLoading(false)
        } catch (error) {
            console.error('Error fetching survey data:', error);
            setLoading(false)
        }
    }

    if (verified) {
        fetchData()
    } else {
        window.location.href=('verify')
    }
  }, [])

  if (!verified) return null

  if (loading) return <div className="fixed inset-0 flex justify-center items-center">
      <div className="border-y border-green-500 w-28 h-28 rounded-full animate-spin">
      </div>
    </div>

  return (
    <div className="p-6 space-y-6">
        <h1 className='text-2xl font-bold text-center'>Assessment Dashboard</h1>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Total Assessment Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Total Assessments</h3>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">{assessments.length}</div>
                </div>

                {/* Detailed Assessment Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Total Detailed Assessments</h3>
                    </div>
                    <div className="text-3xl font-bold text-gray-900">{detailedAssessments.length}</div>
                </div>
        </div>

        {/* Tabs */}
        <div className="w-full">
            <div className="border-b border-gray-200">
                <div className="grid grid-cols-2 w-full">
                    <button
                    className={`py-4 text-center font-medium text-sm ${
                        activeTab === 'assessment'
                        ? 'border-b-2 border-green-500 text-green-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('assessment')}
                    >
                    Assessment
                    </button>
                    <button
                    className={`py-4 text-center font-medium text-sm ${
                        activeTab === 'detailed'
                        ? 'border-b-2 border-green-500 text-green-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('detailed')}
                    >
                    Detailed Assessment
                    </button>
                </div>
            </div>

            {/* Table Content */}
            <div className="mt-6">
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            No
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Time
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Complete
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            View
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {(activeTab === 'assessment' ? assessments : detailedAssessments).map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {index+1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.createdAt || item.submittedAt)
                                    ? new Date((item.createdAt || item.submittedAt)!).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
                                    : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.responseCount}/{Questions.length}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {item.responseCount > 0 ? (
                                    <a
                                    href={activeTab === 'assessment'
                                        ? `assessmentreport?email=${encodeURIComponent(item.email)}`
                                        : `advanceassessmentreport?email=${encodeURIComponent(item.email)}`}
                                    className="text-green-600 hover:text-green-800 inline-flex items-center gap-2"
                                    >
                                    <BsEye className="w-4 h-4" />
                                    <span>View</span>
                                    </a>
                                ) : (
                                    <span className="text-gray-300 inline-flex items-center gap-2 cursor-not-allowed select-none">
                                    <BsEye className="w-4 h-4" />
                                    <span>View</span>
                                    </span>
                                )}
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>    
                    </div>
                </div>
            </div>
      </div>
    </div>
  );
};

export default AssessmentDashboard;